import React, { useState, forwardRef, useEffect } from 'react';
import axios from 'axios'; 
import {useTranslation} from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CREDIT_API_URL, ASSETS_URL } from "../../constants";
import useChatStore from '../../store/useChatStore';
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import { fadeInLeft, fadeInRight, fadeInUp, buttonVariants } from '../../utility/framerMotionAnimations';
import { customerSignIn, fetchLeadsByMobile } from '../../services/leadService';
import showToast from '../../utility/showToast'; 
import useUtmStore from '../../store/useUtmStore';
import getClientInfo from '../../utility/getClientInfo';

const FormSection = forwardRef(({ selectedValue }, ref) => {
     const [isMobileValid, setIsMobileValid] = useState(false);
    const { t } = useTranslation('homepage');
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        productType: "",
        loanAmount: "",
        mobile: "",
        isConsentAccepted:false
    });

    const setCustomer = useChatStore((state) => state.setCustomer);
    const setAuthToken = useChatStore((state) => state.setAuthToken);
    const setSuppressAutoRedirect = useChatStore((state) => state.setSuppressAutoRedirect);

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const utm = useUtmStore(state => state.utm || {});
    const { browserName, deviceType, userAgent } = getClientInfo();
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "loanAmount" || name === "mobile" ? parseInt(value, 10) || "" : value,
        });
        if (name === "mobile") {
            setIsMobileValid(/^[6-9]\d{9}$/.test(value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const messageDiv = document.getElementById("frm-error");
        try {
            const payload = {
                ...formData,
                utmCampaign: utm.utmCampaign || "GrowBizh",
                utmSource: utm.utmSource || null,
                utmMedium: utm.utmMedium || null,
                browserName: browserName || null,
                deviceType: deviceType || null,
            };
            const response = await fetch(`${CREDIT_API_URL}create-lead`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const result = await response.json();

            if (
                response.status === 200 ||
                response.status === 400 ||
                (response.status === 409 && (formData.productType === "Commercial Vehicle Loan" || formData.productType === "Business Loan"))
                ) {
                setLeadData(result.data);
                const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
                    mobile: formData.mobile,
                    productType: formData.productType,
                });
                if (otpResponse.status === 200) {
                    setShowOtpModal(true);
                } else {
                    throw new Error("OTP generation failed.");
                }
            } else {
                messageDiv.style.color = "red";
                messageDiv.textContent = result.message || "Something went wrong.";
                messageDiv.style.display = "block";
            }
        } catch (error) {
            messageDiv.style.color = "red";
            messageDiv.textContent = error.message;
            messageDiv.style.display = "block";
        }
        setTimeout(() => messageDiv.style.display = "none", 3000);
    };

    // Helper function to ensure chat is initialized for a lead (for form submission flow)
    const ensureChatInitialized = async (lead) => {
        if (!lead?.chatSessionId) {
            console.warn('[FormSection] No chatSessionId to initialize chat for');
            return;
        }

        try {
            // Step 1: Check if chat already exists using session API
            console.log('[FormSection] Checking if chat exists for session:', lead.chatSessionId);
            const checkResp = await axios.get(`${CREDIT_API_URL}chat-history/session/${lead.chatSessionId}`);
            
            if (checkResp.data && Array.isArray(checkResp.data.data) && checkResp.data.data.length > 0) {
                console.log('[FormSection] Chat already initialized, skipping initialization');
                return; // Chat already exists
            }

            // Step 2: Chat doesn't exist - initialize it with initial Bizzie messages sequence
            console.log('[FormSection] Chat not found, initializing with Bizzie welcome sequence');
            
            const baseMessageData = {
                chatSessionId: lead.chatSessionId,
                customerId: lead.customerId,
                loanRequestId: lead.loanRequestId || lead.id,
                sentBy: 'bot',
                responseType: 'normal',
                kycDocumentId: null,
                attachmentPath: null
            };

            // Message 1: Welcome message
            await axios.post(`${CREDIT_API_URL}chat-history`, {
                ...baseMessageData,
                messageText: 'Hey there! 👋 I\'m Bizzie, your loan buddy!'
            });

            // Message 2: Loan application details
            const loanAmount = lead.loanAmount || formData.loanAmount || '0';
            const productType = lead.productType || formData.productType || 'LOAN';
            await axios.post(`${CREDIT_API_URL}chat-history`, {
                ...baseMessageData,
                messageText: `You have applied for ₹${loanAmount} under ${productType}.`
            });

            // Message 3: Ask for applicant type
            await axios.post(`${CREDIT_API_URL}chat-history`, {
                ...baseMessageData,
                messageText: 'Please select how you\'re applying for the loan: as an Individual or a Company.'
            });

            console.log('[FormSection] Chat initialized successfully with 3 messages');
        } catch (err) {
            console.error('[FormSection] ensureChatInitialized failed:', err);
            // Don't throw - allow flow to continue even if initialization fails
        }
    };

    const handleOtpSuccess = async () => {
        try {
            console.log("[DEBUG] leadData in handleOtpSuccess:", leadData);
            if (!leadData) throw new Error("Lead data missing. Please try again. (leadData is null or undefined)");
            
            // Step 1: Call customer sign-in API to get dynamic token
            const mobile = leadData.mobile || formData.mobile;
            console.log("[DEBUG] Signing in customer with mobile:", mobile);
            
            const signInResult = await customerSignIn(mobile);
            
            // Step 2: Handle sign-in result
            if (signInResult.success && signInResult.token) {
                // Returning customer - prefer using leadData returned from create-lead (contains chatSessionId)
                console.log("[DEBUG] Customer signed in; preferring create-lead payload if available");
                setAuthToken(signInResult.token);

                // If we have leadData from create-lead, use it directly to set session and route
                    if (leadData && (leadData.chatSessionId || leadData.loanRequestId || leadData.id)) {
                        console.log("[DEBUG] Using leadData to resume (will check totalRecords before routing):", leadData);
                        // Determine mobile for lead lookup
                        const lookupMobile = leadData.mobile || formData.mobile;
                        // Fetch leads to get authoritative totalRecords
                        let leadsResultForDecision = null;
                        try {
                            leadsResultForDecision = await fetchLeadsByMobile(lookupMobile, signInResult.token);
                        } catch (err) {
                            console.warn("[DEBUG] fetchLeadsByMobile failed while deciding routing; falling back to single-lead resume", err);
                        }

                        const totalRecords = Number(leadsResultForDecision?.totalRecords || leadsResultForDecision?.count || (Array.isArray(leadsResultForDecision?.data) ? leadsResultForDecision.data.length : 0)) || 0;
                        console.log("[DEBUG] Decision totals - totalRecords:", totalRecords, "count:", leadsResultForDecision?.count);

                        // Ensure chat is initialized for this lead (form submission flow)
                        // This is critical for duplicate leads (totalRecords > 1) where chat may not exist yet
                        if (formData.productType === "Commercial Vehicle Loan" || formData.productType === "Business Loan") {
                            await ensureChatInitialized(leadData);
                        }

                        // If multiple leads exist, prefer dashboard
                        if (totalRecords > 1) {
                            console.log("[DEBUG] Multiple leads exist; redirecting to dashboard instead of resuming chat");
                            setCustomer(signInResult.customer || {});
                            showToast("Welcome back! Redirecting to your dashboard...", "success");
                            setShowOtpModal(false);
                            navigate('/dashboard');
                            return;
                        }

                        // Otherwise proceed to resume using leadData
                        setCustomer({
                            ...(signInResult.customer || {}),
                            ...leadData,
                            chatSessionId: leadData?.chatSessionId || null,
                            loanRequestId: leadData?.loanRequestId || leadData?.id || null,
                        });
                        useChatStore.setState({
                            chatSessionId: leadData?.chatSessionId || null,
                            loanId: leadData?.loanRequestId || leadData?.id || null,
                        });
                        // Ensure chat history is loaded before navigating so inputs render immediately
                        try {
                            const sessionIdToFetch = leadData?.chatSessionId || leadData?.loanRequestId || leadData?.id || null;
                            if (sessionIdToFetch) {
                                await useChatStore.getState().fetchChatHistory(sessionIdToFetch);
                            }
                        } catch (fetchErr) {
                            console.warn('[FormSection] fetchChatHistory before navigate failed:', fetchErr);
                        }
                        setShowOtpModal(false);

                        if (formData.productType === "Commercial Vehicle Loan" || formData.productType === "Business Loan") {
                            // Resume in Bizzie chat for these product types
                            showToast("Welcome back! Resuming your application...", "success");
                            // Suppress Bizzie auto-redirect check briefly to avoid flicker
                            if (typeof setSuppressAutoRedirect === 'function') {
                                setSuppressAutoRedirect(5000);
                            } else {
                                useChatStore.getState().setSuppressAutoRedirect(5000);
                            }
                            navigate('/bizzie');
                            return;
                        } else {
                            // For other product types (Two Wheeler Loan, Commercial Equipment Financing,
                            // Inventory or Supply Chain Financing, etc.) show thank-you message
                            const msg = document.getElementById("frm-error");
                            if (msg) {
                                msg.style.color = "green";
                                msg.textContent = "Thanks!! We will get back to you soon.";
                                msg.style.display = "block";
                                setTimeout(() => {
                                    if (msg) {
                                        msg.style.display = "none";
                                    }
                                }, 7000); // hide after 7s
                            }
                            return;
                        }
                } else {
                    // No leadData (login via header or missing create-lead response) -> fetch leads
                    console.log("[DEBUG] No leadData available; fetching leads to determine routing");
                    const leadsResult = await fetchLeadsByMobile(mobile, signInResult.token);
                    const totalRecords = Number(leadsResult?.totalRecords || 0);
                    const leadsCount = Number(leadsResult?.count || (Array.isArray(leadsResult?.data) ? leadsResult.data.length : 0)) || 0;
                    console.log("[DEBUG] leadsResult (raw):", leadsResult, "totalRecords:", totalRecords, "count:", leadsCount);
                    setShowOtpModal(false);

                    if (totalRecords > 1) {
                        console.log("[DEBUG] Multiple leads found (totalRecords > 1), redirecting to dashboard");
                        setCustomer(signInResult.customer);
                        showToast("Welcome back! Redirecting to your dashboard...", "success");
                        navigate('/dashboard');
                        return;
                    }

                    if (totalRecords === 1 || leadsCount === 1) {
                        const singleLead = Array.isArray(leadsResult?.data) ? leadsResult.data[0] : leadsResult?.data;
                        console.log("[DEBUG] Single lead found, resuming in bizzie:", singleLead);
                        setCustomer({
                            ...(signInResult.customer || {}),
                            ...singleLead,
                            chatSessionId: singleLead?.chatSessionId || null,
                            loanRequestId: singleLead?.loanRequestId || singleLead?.id || null,
                        });
                        useChatStore.setState({
                            chatSessionId: singleLead?.chatSessionId || null,
                            loanId: singleLead?.loanRequestId || singleLead?.id || null,
                        });
                        // Preload chat history so Bizzie shows inputs without reload
                        try {
                            const sessionToFetch = singleLead?.chatSessionId || singleLead?.loanRequestId || singleLead?.id || null;
                            if (sessionToFetch) {
                                await useChatStore.getState().fetchChatHistory(sessionToFetch);
                            }
                        } catch (fetchErr) {
                            console.warn('[FormSection] fetchChatHistory for singleLead failed:', fetchErr);
                        }
                        showToast("Welcome back! Resuming your application...", "success");
                        // Suppress Bizzie auto-redirect check briefly to avoid flicker
                        if (typeof setSuppressAutoRedirect === 'function') {
                            setSuppressAutoRedirect(5000);
                        } else {
                            useChatStore.getState().setSuppressAutoRedirect(5000);
                        }
                        navigate('/bizzie');
                        return;
                    }
                    // leadsCount === 0 -> proceed as new customer flow below
                }
            } else if (signInResult.isNewCustomer) {
                // New customer - no existing leads (404 from API)
                console.log("[DEBUG] New customer detected, proceeding with chat flow");
                await setCustomer(leadData);
                useChatStore.setState({ chatSessionId: leadData.chatSessionId, loanId: leadData.loanRequestId });
                
                if (formData.productType === "Commercial Vehicle Loan" || formData.productType === "Business Loan") {
                    if (typeof setSuppressAutoRedirect === 'function') {
                        setSuppressAutoRedirect(5000);
                    } else {
                        useChatStore.getState().setSuppressAutoRedirect(5000);
                    }
                    navigate('/bizzie');
                } else {
                    const msg = document.getElementById("frm-error");
                    if (msg) {
                        msg.style.color = "green";
                        msg.textContent = "Thanks!! We will get back to you soon.";
                        msg.style.display = "block";
                        setTimeout(() => {
                            if (msg) msg.style.display = "none";
                        }, 7000);
                    }
                }
            } else {
                // Sign-in failed for other reasons
                throw new Error(signInResult.message || "Failed to verify customer status");
            }
        } catch (err) {
            console.error("[ERROR] in handleOtpSuccess:", err);
            showToast(err.message || "OTP verification failed. Please try again.", "error");
        }
    };

    useEffect(() => {
        if (selectedValue) {
            setFormData((prevData) => ({
                ...prevData,
                productType: selectedValue,
            }));
        }
        const resetChat = useChatStore.getState().resetChat;
        if (typeof resetChat === 'function') {
            resetChat();
        } else {
            useChatStore.setState({ chatSessionId: null, loanId: null, chatHistory: [] });
        }
    }, [selectedValue]);

    const handleSelectFocus = (e) => {
        e.target.style.color = 'black';
    };

    const handleSelectBlur = (e) => {
        if (!e.target.value) {
            e.target.style.color = '#9A9A9A';
        }
    };
    return (
        <section className='section-container invoice-form-section-mobile mt-8' ref={ref}>
            <div className="invoice-form-section-container">
                <motion.div 
                    className="left-invoice-form-section-container"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                    variants={fadeInLeft}
                >
                    <motion.div 
                        className="left-invoice-form-section-container-title"
                        variants={fadeInUp}
                    >
                        {t('form-section-title')}
                    </motion.div>
                    <motion.p 
                        className="left-invoice-form-section-container-content mt-1"
                        variants={fadeInUp}
                        transition={{ delay: 0.2 }}
                    >
                    {t('form-section-content')}
                    </motion.p>
                    
                    
                </motion.div>
                <motion.div 
                    className="right-invoice-form-section-container"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                    variants={fadeInRight}
                >
                 
                    <motion.div 
                        className="form-section"
                        variants={fadeInUp}
                    >
                        <form className="form-horizontal" onSubmit={handleSubmit}>
                            <div className="form-group mt-1">
                                <h3>{t('form-title',{ns:'homepage'})} </h3>
                            </div>
                            <div className="form-group mt-1 input-group">
                            <img src={`${ASSETS_URL}images/bulb.svg`} alt="" />
                                <select className="form-control" required name="productType" value={formData.productType} onChange={handleChange} onFocus={handleSelectFocus}
                                onBlur={handleSelectBlur}>
                                    <option value="" disabled>{t('form-selection-option1')}</option>
                                    <option value="Commercial Vehicle Loan">{t('form-selection-option2')}</option>
                                    <option value="Two Wheeler Loan">{t('form-selection-option3')}</option>
                                    <option value="Business Loan">{t('form-selection-option4')}</option>
                                    <option value="Commercial Equipment Financing">{t('form-selection-option5')}</option>
                                    <option value="Inventory or Supply Chain Financing">{t('form-selection-option6')}</option>
                                </select>
                            </div>
                            <div className="form-group mt-1 input-group">
                                <img src={`${ASSETS_URL}images/ruppe.svg`} alt="" />
                                <input type="text" className="form-control input-field" required placeholder="" name="loanAmount" value={formData.loanAmount}
                                onChange={handleChange} autoComplete="off" id="loan-amount"/>
                                <label htmlFor="loan-amount" className="placeholder">{t('form-placeholder1')}</label>
                            </div>
                            <div className="form-group mt-1 input-group">
                            <img src={`${ASSETS_URL}images/mobile.svg`} alt="" />
                                <input type="text" className={`form-control ${isMobileValid ? 'valid' : ''} input-field`} required placeholder="" name="mobile" value={formData.mobile}
                                 onChange={handleChange} autoComplete="off" id="contact" maxLength={10} minLength={10} />
                                 <label htmlFor="contact" className="placeholder">{t('form-placeholder2')}</label>
                            </div>
                            <div className="form-group consent-group">
                                <input
                                type="checkbox"
                                id="consent"
                                name="isConsentAccepted"
                                checked={formData.isConsentAccepted}
                                onChange={e =>
                                    setFormData(prev => ({
                                    ...prev,
                                    isConsentAccepted: e.target.checked
                                }))
                                }
                                />
                                <label htmlFor="consent" style={{ margin: 0, cursor: 'pointer' }}>
                                    {t('form-description')}
                                </label>
                            </div>
                            <div className="form-group mt-1" id='frm-error'></div>
                            <motion.div 
                                className="form-group mt-1 mb-1"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <button className="btn-small btn-primary btn-form" disabled={!isMobileValid || !formData.isConsentAccepted}>{t('form-button')}</button>
                            </motion.div>
                        </form>
                    </motion.div>
                </motion.div>
            </div>
             <OtpVerificationModal
                show={showOtpModal}
                mobile={formData.mobile}
                productType={formData.productType}
                onClose={() => setShowOtpModal(false)}
                onVerified={handleOtpSuccess}
            />
        </section>
    
    );
})

export default FormSection;